/* eslint-disable @typescript-eslint/no-empty-function */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { OverviewMovableComponents } from '../utils/constants/constants';

/**
 * TODO clean this file once account refresh page is on prod
 */
export interface awardDetailsType {
  count: number;
  expirationDate: string;
}

export interface FreeNightDetails {
  awardType?: {
    code?: string;
    label?: string;
    description?: string;
    enumCode?: string;
  };
  expirationDate?: string;
  isCancellable?: boolean;
  issueDate?: string;
  numberOfNights?: number;
  points?: number;
}

export interface FreeNightAwardsInfo {
  total?: number;
  edges?: {
    node?: FreeNightDetails;
  }[];
}
export interface OverviewState {
  uxlTriggered: boolean;
  componentMoved: string;
  awardsCount: number;
  suiteNightAwardInfo?: Array<awardDetailsType>;
  components: ComponentDetails;
  cardModalId: string;
  freeNightAwardInfo: FreeNightAwardsInfo;
  setFreeNightAwardInfo: (value: FreeNightAwardsInfo) => void;
  setCardModalId: (value: string) => void;
  setAwardsCount: (count: number, details: Array<awardDetailsType>) => void;
  setComponentMoved: (movedCompId: string) => void;
  setComponentHasData: (component: OverviewMovableComponents, hasData: boolean) => void;
  setComponentHasError: (component: OverviewMovableComponents, hasError: boolean) => void;
  setComponentIsLoading: (component: OverviewMovableComponents, isLoading: boolean) => void;
}

export type ComponentDetails = {
  [key: string]: ComponentProperties;
};

export type ComponentProperties = {
  hasData: boolean;
  hasError: boolean;
  isLoading: boolean;
};

const initialPropertyState: ComponentProperties = { hasData: false, hasError: false, isLoading: false };

const initialState: OverviewState = {
  uxlTriggered: false,
  componentMoved: '',
  awardsCount: 0,
  components: {
    [OverviewMovableComponents.UPCOMING_TRIPS]: { ...initialPropertyState },
    [OverviewMovableComponents.RECENT_ACTIVITIES]: { ...initialPropertyState },
    [OverviewMovableComponents.EARNED_REWARDS]: { ...initialPropertyState },
    [OverviewMovableComponents.PROMOTIONS]: { ...initialPropertyState },
  },
  cardModalId: '',
  freeNightAwardInfo: {},
  setFreeNightAwardInfo: () => {},
  setCardModalId: () => {},
  setAwardsCount: () => {},
  setComponentMoved: () => {},
  setComponentHasData: () => {},
  setComponentHasError: () => {},
  setComponentIsLoading: () => {},
};

const overviewStore: StateCreator<OverviewState> = set => {
  return {
    ...initialState,
    setAwardsCount: (count: number, details?: Array<awardDetailsType>) => {
      set({ awardsCount: count, suiteNightAwardInfo: details });
    },
    setComponentMoved: (movedCompId: string) => {
      set({ componentMoved: movedCompId });
    },
    setComponentHasData: (component: OverviewMovableComponents, hasData: boolean) => {
      set((state: OverviewState) => ({
        ...state,
        components: {
          ...state.components,
          [component]: { hasData, hasError: false, isLoading: false },
        },
      }));
    },
    setComponentHasError: (component: OverviewMovableComponents, hasError: boolean) => {
      set((state: OverviewState) => ({
        ...state,
        components: {
          ...state.components,
          [component]: { hasError, hasData: false, isLoading: false },
        },
      }));
    },
    setComponentIsLoading: (component: OverviewMovableComponents, isLoading: boolean) => {
      set((state: OverviewState) => {
        let isUxlTriggered = state.uxlTriggered;
        // uxlTriggered is set to true only when setComponentIsLoading is called by 1st movable component from OverviewMovableComponents enum
        if (!state.uxlTriggered) {
          isUxlTriggered = component === Object.values(OverviewMovableComponents)[0];
        }
        return {
          ...state,
          uxlTriggered: isUxlTriggered,
          components: {
            ...state.components,
            [component]: { isLoading, hasError: false, hasData: false },
          },
        };
      });
    },

    setCardModalId: (value: string) => {
      set({ cardModalId: value });
    },

    setFreeNightAwardInfo: (value: FreeNightAwardsInfo) => {
      set({ freeNightAwardInfo: value });
    },
  };
};

export const useOverviewStore = createAppStore(overviewStore, {
  usePersistentStore: false,
});

/**
 * Account refresh store
 * start from here
 */

export type MessageEntryType = {
  [key: string]: {
    isBirthdayMessageRemove?: string;
    isEarningMessageRemove?: {
      propertyId?: string;
      stayDate?: string;
    };
    isLifetimeMessageRemove?: string;
    isRenewMessageRemove?: string;
    isNewAchieverMessageRemove?: string;
    altCustId?: string;
    storedMemberLevel?: string;
    storedMemberLiftimeCode?: string;
    savedAt?: string;
  };
};

export type AccountPersistentRefreshState = {
  /**
   * defaine types here
   */

  storedInfoForInlineMessage?: MessageEntryType;
  storedInfoForInlineModalMessage?: MessageEntryType;
  updateValuesInAccountRefreshStore: (val?: Partial<AccountPersistentRefreshState>) => void;
};

const AccountPersistentRefreshInitialState: AccountPersistentRefreshState = {
  updateValuesInAccountRefreshStore: () => {},
};

const AccountPersistentRefreshStore: StateCreator<AccountPersistentRefreshState> = set => {
  /**
   * use this store if we have to save any information on local storage
   */

  return {
    ...AccountPersistentRefreshInitialState,
    updateValuesInAccountRefreshStore: (value?: Partial<AccountPersistentRefreshState>) => {
      /**
       * in any case we need to update single object
       */
      set(prevState => ({ ...prevState, ...value }));
    },
  };
};
export const useAccountRefreshPersistantStore = createAppStore(AccountPersistentRefreshStore, {
  usePersistentStore: true,
  useLocalStorage: true,
  persistentStoreName: 'AccountRefreshPageLocalStore',
});

/**
 * account refresh non persistent store start from here
 *
 *
 *
 *
 *
 * space for future use
 */

export type EarningSummaryType = {
  // Property Information
  propertyName: string;
  propertyId: string;
  brandId?: string;
  propertyImageUrl?: string;

  // Stay Dates
  stayStartDate: string; // ISO date string
  stayEndDate: string; // ISO date string
  postDate: string; // When earnings were posted (ISO date string)
  numberOfNights: number;

  // Earnings Breakdown
  totalEliteNightCredits: number; // ENCs earned
  totalPoints: number; // Total points earned
  basePoints: number; // Base points from stay
  elitePoints: number; // Elite bonus points
  bonusPoints: number; // Extra promotional points
  currencyCode?: string;
  // Activity Metadata
  isQualifyingActivity: boolean;
  activityType: string; // e.g., "STAY", "QUALIFYING"
  activityDescription?: string;
} | null;
type IsComponentLoading = {
  [OverviewMovableComponents.UPCOMING_TRIPS]: boolean;
  [OverviewMovableComponents.EARNINGS_SUMMARY]: boolean;
};
type AccountNonPersistentRefreshState = {
  isEarningSummaryModalOpen: boolean;
  isMemberBenefitsModalOpen: boolean;
  isUpcomingTripWithin30days?: boolean;
  isComponentLoading: IsComponentLoading;
  earningSummary?: EarningSummaryType;
  earningSummaryPropertyImageUrl?: string | null;
  updateValuesInNonPersistentStore: (value: Partial<AccountNonPersistentRefreshState>) => void;
  setAccountRefreshComponentLoading: (value: Partial<IsComponentLoading>) => void;
};

const AccountNonPersistentRefreshInitialState: AccountNonPersistentRefreshState = {
  isEarningSummaryModalOpen: false,
  isMemberBenefitsModalOpen: false,
  earningSummary: undefined,
  isUpcomingTripWithin30days: false,
  isComponentLoading: {
    [OverviewMovableComponents.UPCOMING_TRIPS]: true,
    [OverviewMovableComponents.EARNINGS_SUMMARY]: true,
  },
  updateValuesInNonPersistentStore: () => {},
  setAccountRefreshComponentLoading: () => {},
};
const AccountNonPersistentRefreshStore: StateCreator<AccountNonPersistentRefreshState> = set => {
  /**
   * use this store if we don't have to save any information on local storage
   */
  return {
    ...AccountNonPersistentRefreshInitialState,

    updateValuesInNonPersistentStore: (value?: Partial<AccountNonPersistentRefreshState>) => {
      set(prevState => ({ ...prevState, ...value }));
    },
    setAccountRefreshComponentLoading: (value: Partial<IsComponentLoading>) => {
      set(prevState => ({
        ...prevState,
        isComponentLoading: { ...prevState.isComponentLoading, ...value },
      }));
    },
  };
};
export const useAccountRefreshNonPersistentStore = createAppStore(AccountNonPersistentRefreshStore, {
  usePersistentStore: false,
});
